import classNames from 'classnames';
import { FC, useEffect, useMemo, useState } from 'react';
import { isMobileOnly, isTablet } from 'react-device-detect';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSelector } from 'react-redux';
import SignUpContent from 'src/components/base-components/Modals/AuthModal/components/SignUpContent';
import { Loader } from 'src/components/common/Loader';
import { checkDeviceCompatibility, detectDevice, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { getAuth } from 'src/services/configsApi';
import { getAuthImages } from 'src/services/dataApi';
import PersisterService from 'src/services/PersisterService';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setDniResult, toggleActivateFinalNextStep, toggleActivateNextStep } from 'src/store/user/actions';
import { ISignUpRes } from 'src/types/res-dto/authorization';
import { RootState } from 'src/types/store-types';
import './styles.scss';

export const Signup: FC = () => {
  const isPapi = process.env.REACT_APP_WEBSITENAME === 'PapiGames';
  const [signUpModalData, setSignUpModalData] = useState<ISignUpRes>({});
  const [imgSrc, setImgSrc] = useState<string>('');
  const device = detectDevice();

  const modalContent = 'registration';
  const { showMenu } = useSelector((state: RootState) => state.mobile);
  const deviceCompatibilityStatus = checkDeviceCompatibility().compatible;
  const _openAuthModal = useConnectedAction(openAuthModal);
  const _setDniResult = useConnectedAction(setDniResult);
  const _openDialog = useConnectedAction(openDialog);
  const _toggleActivateNextStep = useConnectedAction(toggleActivateNextStep);
  const _toggleActivateFinalNextStep = useConnectedAction(toggleActivateFinalNextStep);

  const cleanSignupState = (): void => {
    _setDniResult({});
    _toggleActivateNextStep(false);
    _toggleActivateFinalNextStep(false);

    if (!deviceCompatibilityStatus) {
      _openDialog({
        dialogType: EnumDialogsKeys.DEVICE_COMPATIBILITY_POPUP,
        dialogProps: { data: {} },
      });
    }
  };

  const setContent = (action: 'open' | 'close', content: 'login' | 'registration' | 'forgot' | null): void => {
    cleanSignupState();
    const menuItem = localStorage.getItem('menuItem');
    if (PersisterService.hasPersistValue('navigation') && menuItem) {
      const res = PersisterService.getPersistValue('navigation');
      const _navItemData = res.find((f: { name: string | null }) => f.name === menuItem);
      if (_navItemData.needLogIn) {
        setBodyOverflow('unset');
        _openAuthModal(null);
        localStorage.removeItem('menuItem');
      }
    } else {
      !showMenu && setBodyOverflow(action === 'open' ? 'unset' : 'set');
      _openAuthModal(action === 'open' ? content : null);
    }
  };

  const fetchAuthData = (): void => {
    getAuth().then((res: any) => {
      if (res.success) {
        setSignUpModalData(res.result.signUp);
        sessionStorage.setItem(
          'authConfigs',
          JSON.stringify([
            res.result.signIn,
            res.result.forgotPassword,
            res.result.signUp,
            { login: res.result.login, registration: res.result.registration },
          ])
        );
      }
    });
  };

  const getAuthImgSrc = (): void => {
    setImgSrc('');

    !isTablet &&
      getAuthImages().then((res: any) => {
        if (!!res?.data) {
          setImgSrc(
            process.env.REACT_APP_FILE_STORAGE_ADDRESS +
              res.data?.attributes[
                device +
                  (modalContent as string).charAt(0).toUpperCase() +
                  (modalContent as string).slice(1) +
                  'Banners'
              ].data?.[0].attributes.url.substring(1)
          );
        }
      });
  };

  const image = useMemo(() => {
    if (!isTablet) {
      return (
        <div
          className={classNames('content_img', {
            mobile_content_img: isMobileOnly && isPapi,
          })}
        >
          {imgSrc ? <LazyLoadImage src={imgSrc} className="img" alt={modalContent} /> : <Loader size="lg" />}
        </div>
      );
    }

    return null;
  }, [modalContent, imgSrc]);

  useEffect(() => {
    let result = null;

    if (PersisterService.hasPersistValue('authConfigs')) {
      result = PersisterService.getPersistValue('authConfigs');

      if (result[2]) setSignUpModalData(result[2]);
    } else {
      fetchAuthData();
    }
  }, []);

  useEffect(() => {
    signUpModalData?.type === 'withImage' && getAuthImgSrc();
  }, [signUpModalData?.type]);

  useEffect(() => {
    cleanSignupState();

    return () => cleanSignupState();
  }, []);

  return (
    <div className="signupPageRoot page_container">
      <div className="signupPageContent">
        <div className="signupPageImage">{image}</div>

        <div className="signupPageForm">
          <SignUpContent
            setContent={setContent}
            forgotLink={signUpModalData?.forgotLink}
            signIn={signUpModalData?.signIn}
            registrationSteps={signUpModalData?.registrationSteps}
            styles={signUpModalData?.styles}
          />
        </div>
      </div>
    </div>
  );
};
